import { z } from 'zod';

// =============================================================================
// core-one API response models for the Masterdata Sync Monitoring page (fn-1).
//
// All schemas use z.looseObject so unexpected/new API fields are tolerated
// (the core-one contract evolves independently). Wire types were verified
// against the core-one backend (see spec fn-1 "Resolved via Codebase"):
//   - timestamps (lastRequestAt/lastCallbackAt, meta.updatedAt) are epoch-ms
//     NUMBERS, not ISO strings.
//
// IMPORTANT: the core-one API OMITS keys it has no value for rather than
// sending null (e.g. a table with no failure has no `failure` key at all; a
// table with no downstream targets has no `itemSync`/`targets`; a per-table
// downstream target carries only `name`+`status`). Optional/absent fields must
// therefore be `.nullish()` (nullable AND optional), NOT `.nullable()` — a
// `.nullable()` field REQUIRES the key to be present, so a missing key fails
// validation and (via parseResponseItem's throw) blanks the whole view. Only
// structural identifiers that are always present stay required.
// =============================================================================

// --- Shared sub-schemas ------------------------------------------------------

export const DownstreamTargetStatusSchema = z.looseObject({
	name: z.string().nullish(),
	version: z.string().nullish(),
	status: z.string().nullish(),
	synchronized: z.boolean().nullish(),
	queuedItemCount: z.number().nullish(),
	completedItemCount: z.number().nullish(),
	pendingItemCount: z.number().nullish(),
	targetObservedRowCount: z.number().nullish(),
	targetObservedMaxRowVersion: z.number().nullish(),
	tableCount: z.number().nullish(),
	synchronizedTableCount: z.number().nullish(),
	pendingTableCount: z.number().nullish(),
	failedTableCount: z.number().nullish(),
});
export type IDownstreamTargetStatus = z.infer<typeof DownstreamTargetStatusSchema>;

// --- Environment schemas -----------------------------------------------------

export const EnvironmentSynchronizationProgressSchema = z.looseObject({
	reason: z.string().nullish(),
	estimatedPendingTableCount: z.number().nullish(),
});

export const EnvironmentSynchronizationSchema = z.looseObject({
	disabled: z.boolean().nullish(),
	status: z.string().nullish(),
	error: z.string().nullish(),
	progress: EnvironmentSynchronizationProgressSchema.nullish(),
});

export const EnvironmentLifecycleInfoSchema = z.looseObject({
	generation: z.number().nullish(),
	state: z.string().nullish(),
	sourceVersion: z.string().nullish(),
	error: z.string().nullish(),
});

// A repair core-one found for the environment's current lifecycle generation and left to an
// operator (only present while it applies). `recommendedAction` is 'Repair' (POST .../repair) or
// 'ResetTables' (a synchronization reset filtered to `tables`); the other one is the fallback.
export const EnvironmentRepairNeededSchema = z.looseObject({
	category: z.string().nullish(),
	lifecycleGeneration: z.number().nullish(),
	tables: z.array(z.string()).nullish(),
	reason: z.string().nullish(),
	recommendedAction: z.string().nullish(),
	recommendedActionReason: z.string().nullish(),
	detectedAt: z.number().nullish(),
	detectedBy: z.string().nullish(),
});
export type IEnvironmentRepairNeeded = z.infer<typeof EnvironmentRepairNeededSchema>;

export const EnvironmentDetailSchema = z.looseObject({
	self: z.string(),
	name: z.string(),
	version: z.string().nullish(),
	engine: z.union([z.string(), z.number()]).nullish(),
	engineId: z.union([z.string(), z.number()]).nullish(),
	Engine: z.union([z.string(), z.number()]).nullish(),
	EngineId: z.union([z.string(), z.number()]).nullish(),
	ready: z.boolean().nullish(),
	synchronized: z.boolean().nullish(),
	stable: z.boolean().nullish(),
	databaseVersion: z.number().nullish(),
	// The default-environment endpoint (/one/environment) can return a slimmer
	// environment than the /one/environments/:id detail — these nested blocks
	// may be absent, so keep them nullish and guard access in the template.
	synchronization: EnvironmentSynchronizationSchema.nullish(),
	lifecycleInfo: EnvironmentLifecycleInfoSchema.nullish(),
	targets: z.array(DownstreamTargetStatusSchema).nullish(),
	repairNeeded: EnvironmentRepairNeededSchema.nullish(),
});
export type IEnvironmentDetail = z.infer<typeof EnvironmentDetailSchema>;

export const EnvironmentSummarySchema = z.looseObject({
	self: z.string(),
	name: z.string(),
	engine: z.union([z.string(), z.number()]).nullish(),
	engineId: z.union([z.string(), z.number()]).nullish(),
	Engine: z.union([z.string(), z.number()]).nullish(),
	EngineId: z.union([z.string(), z.number()]).nullish(),
});
export type IEnvironmentSummary = z.infer<typeof EnvironmentSummarySchema>;

// --- Status endpoint schemas (GET /one/environments/:id/status) --------------

export const ItemSyncProgressSchema = z.looseObject({
	queuedItemCount: z.number().nullish(),
	processedItemCount: z.number().nullish(),
	estimatedProcessedItemCount: z.number().nullish(),
	sourceSynchronizationComplete: z.boolean().nullish(),
	coreProcessingComplete: z.boolean().nullish(),
	stage: z.string().nullish(),
	reason: z.string().nullish(),
	stale: z.boolean().nullish(),
	staleForMs: z.number().nullish(),
	pending: z.boolean().nullish(),
});
export type IItemSyncProgress = z.infer<typeof ItemSyncProgressSchema>;

export const TableSynchronizationSchema = z.looseObject({
	ignore: z.boolean().nullish(),
	requisitionId: z.number().nullish(),
	rowVersion: z.number().nullish(),
	// Verified: epoch-ms numbers (Table.LastSynchronizationRequest/Callback are long?), not strings.
	lastRequestAt: z.number().nullish(),
	lastCallbackAt: z.number().nullish(),
	delay: z.number().nullish(),
	error: z.string().nullish(),
});
export type ITableSynchronization = z.infer<typeof TableSynchronizationSchema>;

// --- Drift audit schemas (the `audit` and `cleanup` objects of GET .../status) -------

// A table's last completed drift audit run. `status` is the table's audit status (differing, aligned,
// in_progress, ...); every other field describes the last completed run.
export const TableAuditSchema = z.looseObject({
	status: z.string().nullish(),
	runId: z.string().nullish(),
	dispatchedAt: z.number().nullish(),
	completedAt: z.number().nullish(),
	trigger: z.string().nullish(),
	missingCount: z.number().nullish(),
	mismatchedCount: z.number().nullish(),
	extraCount: z.number().nullish(),
	offVersionCount: z.number().nullish(),
	replayedCount: z.number().nullish(),
	failedCount: z.number().nullish(),
	deferredCount: z.number().nullish(),
	exhaustedCount: z.number().nullish(),
	hasExhaustedRepairs: z.boolean().nullish(),
	// The replay of skipped items requested for the last completed run; sent/superseded are absent until its job has run.
	replayedSkipped: z
		.looseObject({
			at: z.number().nullish(),
			auditRunId: z.string().nullish(),
			sent: z.number().nullish(),
			superseded: z.number().nullish(),
		})
		.nullish(),
});
export type ITableAudit = z.infer<typeof TableAuditSchema>;

const CleanupKindCountsSchema = z.looseObject({
	removal: z.number().nullish(),
	sweep: z.number().nullish(),
	total: z.number().nullish(),
});

export const TableCleanupSummarySchema = z.looseObject({
	available: z.boolean().nullish(),
	unavailableReason: z.string().nullish(),
	hasFailures: z.boolean().nullish(),
	retrying: CleanupKindCountsSchema.nullish(),
	exhausted: CleanupKindCountsSchema.nullish(),
	// The last pass that removed extra rows from core-engine. "issued" is not "removed": the next audit confirms it.
	removalPass: z
		.looseObject({
			outcome: z.string().nullish(),
			cap: z.number().nullish(),
			permitted: z.number().nullish(),
			issued: z.number().nullish(),
			refused: z.number().nullish(),
			outstanding: z.number().nullish(),
			exhaustedSkipped: z.number().nullish(),
			recordedAt: z.number().nullish(),
		})
		.nullish(),
	// What the clean-ups were permitted to act on for the last run (what turning a flag on would act on).
	permitted: CleanupKindCountsSchema.nullish(),
	sweepReport: z
		.looseObject({
			version: z.string().nullish(),
			markedThisRun: z.number().nullish(),
			rescuedThisRun: z.number().nullish(),
			pendingExpiryCount: z.number().nullish(),
			windowEndsAt: z.number().nullish(),
			// True when the report is not the table's current state: run_superseded or expiries_passed.
			stale: z.boolean().nullish(),
			staleReason: z.string().nullish(),
		})
		.nullish(),
});
export type ITableCleanupSummary = z.infer<typeof TableCleanupSummarySchema>;

export const EnvironmentAuditSchema = z.looseObject({
	status: z.string().nullish(),
	hasExhaustedRepairs: z.boolean().nullish(),
	fullPassWeekday: z.string().nullish(),
	lastFullPassAt: z.number().nullish(),
	settings: z
		.looseObject({
			schedule: z.string().nullish(),
			fullPassDays: z.number().nullish(),
			replay: z.looseObject({ enabled: z.boolean().nullish(), capPerTable: z.number().nullish() }).nullish(),
			removal: z.looseObject({ mode: z.string().nullish(), capPerPass: z.number().nullish() }).nullish(),
			sweep: z.looseObject({ mode: z.string().nullish(), windowDays: z.number().nullish() }).nullish(),
		})
		.nullish(),
	nextRun: z.looseObject({ at: z.number().nullish(), fullPass: z.boolean().nullish() }).nullish(),
});
export type IEnvironmentAudit = z.infer<typeof EnvironmentAuditSchema>;

// --- Source drift schemas (the `sourceDrift` object of GET .../status, and .../tables/:table/source-drift-check) ---

// The table's last range-check run. `state` is running, completed or failed; `reason` says why a run failed.
export const SourceDriftLastRunSchema = z.looseObject({
	state: z.string().nullish(),
	startedAt: z.number().nullish(),
	finishedAt: z.number().nullish(),
	rangesChecked: z.number().nullish(),
	leafRanges: z.number().nullish(),
	missingFound: z.number().nullish(),
	resent: z.number().nullish(),
	extraFound: z.number().nullish(),
	expired: z.number().nullish(),
	reason: z.string().nullish(),
});
export type ISourceDriftLastRun = z.infer<typeof SourceDriftLastRunSchema>;

// The on-prem row count against Core One's active items, as the last completed drift audit saw them. A positive
// `difference` is rows missing in Core One, a negative one extra items. With `checkUnavailable`, `exhausted` and
// `lastRun` are left out because the range-check records couldn't be read.
export const TableSourceDriftSchema = z.looseObject({
	sourceCount: z.number().nullish(),
	coreOneCount: z.number().nullish(),
	difference: z.number().nullish(),
	firstSeenAt: z.number().nullish(),
	lastCheckedAt: z.number().nullish(),
	consecutiveAudits: z.number().nullish(),
	exhausted: z.boolean().nullish(),
	checkUnavailable: z.boolean().nullish(),
	lastRun: SourceDriftLastRunSchema.nullish(),
});
export type ITableSourceDrift = z.infer<typeof TableSourceDriftSchema>;

// A row-version span, lower bound exclusive.
const RowVersionSpanSchema = z.looseObject({
	fromExclusive: z.number().nullish(),
	toInclusive: z.number().nullish(),
});

// A leaf range whose counts differ. `missing`/`extra` are absent until the leaf has been read.
export const SourceDriftLeafSchema = RowVersionSpanSchema.extend({
	sourceCount: z.number().nullish(),
	coreOneCount: z.number().nullish(),
	reads: z.array(RowVersionSpanSchema).nullish(),
	missing: z.number().nullish(),
	extra: z.number().nullish(),
	missingSample: z.array(z.string()).nullish(),
	extraSample: z.array(z.string()).nullish(),
	expiryRefused: z.string().nullish(),
});
export type ISourceDriftLeaf = z.infer<typeof SourceDriftLeafSchema>;

// GET .../tables/:table/source-drift-check: the table's latest range-check run with its leaves.
export const SourceDriftRunSchema = z.looseObject({
	runId: z.string().nullish(),
	state: z.string().nullish(),
	reason: z.string().nullish(),
	startedAt: z.number().nullish(),
	finishedAt: z.number().nullish(),
	rangesChecked: z.number().nullish(),
	resent: z.number().nullish(),
	expired: z.number().nullish(),
	attempts: z.number().nullish(),
	leaves: z.array(SourceDriftLeafSchema).nullish(),
});
export type ISourceDriftRun = z.infer<typeof SourceDriftRunSchema>;

// The outcome of POST .../tables/:table/source-drift-check. `reason` is core-one's English text, shown as received.
export type SourceDriftStartResult = { kind: 'started'; runId: string | null } | { kind: 'conflict'; runId: string | null; reason: string | null } | { kind: 'rejected'; reason: string | null } | { kind: 'forbidden' } | { kind: 'notAvailable' } | { kind: 'failed' };

export const StatusTableModelSchema = z.looseObject({
	self: z.string(),
	version: z.string().nullish(),
	status: z.looseObject({ self: z.string() }),
	failure: z.string().nullish(),
	rowCount: z.number().nullish(),
	maxRowVersion: z.number().nullish(),
	itemSync: ItemSyncProgressSchema.nullish(),
	targets: z.array(DownstreamTargetStatusSchema).nullish(),
	synchronization: TableSynchronizationSchema.nullish(),
	audit: TableAuditSchema.nullish(),
	cleanup: TableCleanupSummarySchema.nullish(),
	// A malformed block drops only the table's drift, logged like a response mismatch, and never fails the status.
	sourceDrift: TableSourceDriftSchema.nullish().catch((ctx) => {
		console.error('[SyncMonitoring] Invalid sourceDrift', { issues: ctx.error.issues, received: ctx.input });
		return undefined;
	}),
});
export type IStatusTableModel = z.infer<typeof StatusTableModelSchema>;

export const EnvironmentStatusResponseSchema = z.looseObject({
	self: z.string(),
	targets: z.array(DownstreamTargetStatusSchema).nullish(),
	tables: z.array(StatusTableModelSchema),
	audit: EnvironmentAuditSchema.nullish(),
});
export type IEnvironmentStatusResponse = z.infer<typeof EnvironmentStatusResponseSchema>;

// --- Worker schemas ----------------------------------------------------------

export const WorkerFilterSchema = z.looseObject({
	type: z.looseObject({ self: z.string().nullish() }).nullish(),
	name: z.string().nullish(),
	value: z.string().nullish(),
});
export type IWorkerFilter = z.infer<typeof WorkerFilterSchema>;

export const WorkerModelSchema = z.looseObject({
	self: z.string(),
	name: z.string(),
	// Filters that scope what the worker processes (e.g. a "tag" filter of "TableSync,TableStatus").
	filters: z.array(WorkerFilterSchema).nullish(),
	// Meta.UpdatedAt is epoch-ms (long) when present; not used by the worker display.
	meta: z.looseObject({ updatedAt: z.number().nullish() }).nullish(),
});
export type IWorkerModel = z.infer<typeof WorkerModelSchema>;

export const HeartbeatModelSchema = z.looseObject({
	self: z.string(),
	jobAgentVersion: z.string().nullish(),
	databaseVersion: z.number().nullish(),
	ipAddress: z.string().nullish(),
	hostName: z.string().nullish(),
	dbServerName: z.string().nullish(),
	// The connected core-one database name (e.g. "ACIESONE_JPE_CORE3_DEV").
	dbName: z.string().nullish(),
	// The worker's serialized core-one configuration (a JSON string); shown as a collapsible detail.
	oneConfiguration: z.string().nullish(),
	// The on-prem agent's process id and how long the process has run (seconds); a change shows a restart.
	pid: z.number().nullish(),
	timeSpentAlive: z.number().nullish(),
	// Meta.UpdatedAt is epoch-ms (long); buildWorkerDisplay guards a missing value.
	meta: z.looseObject({ updatedAt: z.number().nullish() }).nullish(),
});
export type IHeartbeatModel = z.infer<typeof HeartbeatModelSchema>;

// --- Error schemas -----------------------------------------------------------

export const TableErrorModelSchema = z.looseObject({
	// `self` is unused by the display (mapErrors uses `table.self`); keep it optional so an error
	// item without a top-level `self` (same id-based shape as transitions) doesn't blank the view.
	self: z.string().nullish(),
	table: z.looseObject({ self: z.string() }),
	counter: z.number().nullish(),
	version: z.string().nullish(),
	lifecycleGeneration: z.number().nullish(),
	databaseName: z.string().nullish(),
	failure: z.string().nullish(),
	failureScope: z.string().nullish(),
	failureKind: z.string().nullish(),
	failureStage: z.string().nullish(),
	isRetriable: z.boolean().nullish(),
	current: z.boolean().nullish(),
	outdated: z.boolean().nullish(),
	// Drift audit findings (failureKind audit*) carry the item and what repair did with it.
	itemKey: z.string().nullish(),
	rowVersion: z.number().nullish(),
	engineRowVersion: z.number().nullish(),
	observedAuditVersion: z.string().nullish(),
	auditS3ObjectVersion: z.string().nullish(),
	repairOutcome: z.string().nullish(),
	repairAttempts: z.number().nullish(),
	repairKind: z.string().nullish(),
	removalIssuedAt: z.number().nullish(),
	marked: z.looseObject({ expiresAt: z.number().nullish(), confirmed: z.boolean().nullish() }).nullish(),
	// When the error was recorded and last updated (epoch-ms), shown on the Errors tab.
	meta: z.looseObject({ createdAt: z.number().nullish(), updatedAt: z.number().nullish() }).nullish(),
});
export type ITableErrorModel = z.infer<typeof TableErrorModelSchema>;

// --- Sync reset schemas ------------------------------------------------------

// The reset response is used only to confirm success (nothing reads its fields), so keep it
// lenient. The real API returns no top-level `environment` (it is nested per-table under
// `tables[].environment`) and shapes `tables` richly — only `self` is guaranteed at the root.
export const SyncResetResponseSchema = z.looseObject({
	self: z.string(),
	environment: z.looseObject({ self: z.string() }).nullish(),
	tables: z.array(z.looseObject({ self: z.string(), tableName: z.string().nullish() })).nullish(),
});
export type ISyncResetResponse = z.infer<typeof SyncResetResponseSchema>;

// --- Environment repair schemas ----------------------------------------------

// POST .../repair (and ?dryRun=true, which returns the same shape without writing anything).
export const EnvironmentRepairResponseSchema = z.looseObject({
	self: z.string(),
	dryRun: z.boolean().nullish(),
	category: z.string().nullish(),
	outcome: z.string().nullish(),
	completed: z.boolean().nullish(),
	affectedTables: z.array(z.string()).nullish(),
	changes: z.array(z.string()).nullish(),
	blockingReasons: z.array(z.string()).nullish(),
	warnings: z.array(z.string()).nullish(),
});
export type IEnvironmentRepairResponse = z.infer<typeof EnvironmentRepairResponseSchema>;

// --- Drift audit action schemas ---------------------------------------------

// A page of one kind of audit findings (GET .../tables/:table/errors?kind=...); `next` is absent on the last page.
export interface IAuditFindingsPage {
	items: ITableErrorModel[];
	total: number | null;
	next: string | null;
}

// POST .../audits with flow "drift": each table is dispatched, or in_progress when another run holds it.
export const DriftAuditResponseSchema = z.looseObject({
	auditRunId: z.string().nullish(),
	tables: z.array(z.looseObject({ self: z.string().nullish(), status: z.string().nullish() })).nullish(),
});
export type IDriftAuditResponse = z.infer<typeof DriftAuditResponseSchema>;

// POST .../audit-replays[?dryRun=true]
export const SkippedReplayResponseSchema = z.looseObject({
	tables: z.array(
		z.looseObject({
			tableName: z.string(),
			auditRunId: z.string(),
			skipped: z.number().nullish(),
			willSendAtMost: z.number().nullish(),
			queued: z.boolean().nullish(),
			blockedReason: z.string().nullish(),
		}),
	),
});
export type ISkippedReplayResponse = z.infer<typeof SkippedReplayResponseSchema>;
export type ISkippedReplayTable = ISkippedReplayResponse['tables'][number];

const CleanupRetryListSchema = z.looseObject({
	items: z
		.array(
			z.looseObject({
				kind: z.string().nullish(),
				itemKey: z.string(),
				attemptsMade: z.number().nullish(),
				attemptLimit: z.number().nullish(),
				exhausted: z.boolean().nullish(),
			}),
		)
		.nullish(),
	truncated: z.boolean().nullish(),
});

// GET .../tables/:table/cleanup — the bounded lists behind a table's `cleanup` summary.
export const TableCleanupDetailSchema = z.looseObject({
	retrying: CleanupRetryListSchema.nullish(),
	exhausted: CleanupRetryListSchema.nullish(),
	refusals: z
		.array(
			z.looseObject({
				kind: z.string().nullish(),
				reason: z.string().nullish(),
				count: z.number().nullish(),
				keys: z.array(z.string()).nullish(),
			}),
		)
		.nullish(),
	// The first 100 items the off-version sweep marked for expiry (removal from One).
	marks: z
		.array(
			z.looseObject({
				itemKey: z.string(),
				observedVersion: z.string().nullish(),
				auditRunId: z.string().nullish(),
				markExpiresAt: z.number().nullish(),
				confirmed: z.boolean().nullish(),
			}),
		)
		.nullish(),
	marksCapped: z.boolean().nullish(),
});
export type ITableCleanupDetail = z.infer<typeof TableCleanupDetailSchema>;
export type ICleanupMark = NonNullable<ITableCleanupDetail['marks']>[number];

// POST .../tables/:table/cleanup-resets
export const CleanupResetResponseSchema = z.looseObject({
	outcome: z.string().nullish(),
	reset: z.number().nullish(),
	message: z.string().nullish(),
});
export type ICleanupResetResponse = z.infer<typeof CleanupResetResponseSchema>;

// POST .../tables/:table/sweep-marks/clear
export const ClearSweepMarksResponseSchema = z.looseObject({
	cleared: z.array(z.string()).nullish(),
	notCleared: z.array(z.looseObject({ key: z.string(), reason: z.string().nullish() })).nullish(),
	stillMarked: z.number().nullish(),
});
export type IClearSweepMarksResponse = z.infer<typeof ClearSweepMarksResponseSchema>;

export interface IClearSweepMarksRequest {
	version: string;
	markedByRunId: string;
	keys: string[];
}

// GET .../audit-report: `url` when the report is ready (200), `retryAfter` seconds while it is built (202).
export const AuditReportResponseSchema = z.looseObject({
	url: z.string().nullish(),
	retryAfter: z.number().nullish(),
});
export type IAuditReportResponse = z.infer<typeof AuditReportResponseSchema>;

// --- Transition schemas ------------------------------------------------------

export const TransitionModelSchema = z.looseObject({
	// Transitions are identified by `id`, not `self` — the API does not send a top-level `self`
	// on transition items (mapTransitions uses `id`), so `self` must be optional.
	self: z.string().nullish(),
	id: z.string(),
	firstSeen: z.number().nullish(),
	lastSeen: z.number().nullish(),
	count: z.number().nullish(),
	generation: z.number().nullish(),
	version: z.string().nullish(),
	source: z.string().nullish(),
	event: z.string().nullish(),
	context: z.string().nullish(),
	expectedLifecycleState: z.string().nullish(),
	desiredLifecycleState: z.string().nullish(),
	previousState: z.string().nullish(),
	nextState: z.string().nullish(),
	action: z.string().nullish(),
	outcome: z.string().nullish(),
	reasonCode: z.string().nullish(),
});
export type ITransitionModel = z.infer<typeof TransitionModelSchema>;

// --- Request types (outgoing — not validated) --------------------------------

export interface ISyncResetRequest {
	filter: { self: string }[];
	rowVersion: number | null;
}

/**
 * Body for POST/PUT /one/environments/:id/workers (WorkerRequestModel). `name` is required; each filter carries a `type` discriminator as a `{ self }` object (the framework's SelfEnum / DiscriminatorConverter requires the object form — a plain string is rejected) plus a name+value pair. Filter names must be unique
 * server-side. Queue/WhiteList are omitted (the API defaults them).
 */
export interface IWorkerCreateRequest {
	name: string;
	filters: { type: { self: string }; name: string; value: string }[];
}

// --- Generic wrappers (cannot be expressed as zod schemas) -------------------

export interface ICollectionResponse<T> {
	self: string;
	items: T[];
}

export interface IDefaultEnvironmentResponse {
	self: string;
	environment: IEnvironmentDetail;
	// Verified: createdAt/updatedAt are epoch-ms numbers. This wrapper is not
	// zod-validated (only `.environment` is parsed), so the typing is advisory.
	meta: { version: number; createdAt: number; updatedAt: number };
}

// --- Display / view models (flattened for the component + GenericTable) ------

export interface ITableRow {
	self: string;
	tableName: string;
	version: string;
	status: string; // derived status label (failure | ignored | stale | synchronizing | pending | synchronized | unknown)
	rowCount: number | null;
	maxRowVersion: number | null;
	maxRowVersionHex: string | null;
	failure: string | null;
	// Item sync progress
	processedCount: number | null;
	queuedCount: number | null;
	stage: string | null;
	reason: string | null;
	sourceSyncComplete: boolean | null;
	coreProcessingComplete: boolean | null;
	stale: boolean | null;
	staleDuration: string | null; // human-readable
	// Downstream targets
	targets: IDownstreamTargetStatus[] | null;
	// Synchronization metadata
	ignore: boolean | null;
	requisitionId: number;
	syncRowVersion: number | null;
	syncRowVersionHex: string | null;
	lastRequestAt: number | null; // epoch-ms
	lastCallbackAt: number | null; // epoch-ms
	delay: number | null;
	delayDisplay: string | null; // human-readable
	syncError: string | null;
	// Core Engine and drift audit columns
	engineLabel: string; // core-engine target status, or "One only" when the table has no engine target
	engineRowCount: number | null;
	auditLabel: string;
	auditIssue: boolean; // the audit could not confirm the table matches core-engine
	notInEngine: boolean; // no core-engine target, or the target is not synchronized
	// Source drift between on-prem and Core One: null without it and for a difference of 0
	sourceDrift: ITableSourceDrift | null;
	sourceDriftLabel: string; // the Source drift column's badge markup, empty without drift
	sourceDriftDifference: number; // what the Source drift column sorts by, 0 without drift
}

/** A row of the Audit tab: a table's last completed drift audit. */
export interface IAuditRow {
	tableName: string;
	status: string; // audit status, never_audited when the API sends none
	statusLabel: string;
	missing: number | null;
	mismatch: number | null;
	extra: number | null;
	offVersion: number | null;
	repairLabel: string;
	exhausted: number | null;
	lastAudit: string;
	audit: ITableAudit | null;
	cleanup: ITableCleanupSummary | null;
	skipped: number; // items the last audit found but did not replay (see skippedCount)
}

/** One heartbeat of a worker's recent history, newest first. */
export interface IHeartbeatRow {
	at: string;
	pid: number | null;
	uptime: string;
	hostName: string | null;
	jobAgentVersion: string | null;
	restarted: boolean;
}

export interface IWorkerDisplay {
	name: string;
	id: string; // worker id (trailing GUID of the worker self link)
	self: string;
	lastHeartbeat: Date | null; // derived from heartbeat meta.updatedAt (epoch-ms)
	lastHeartbeatFormatted: string; // formatted via aciesDateFormat(date, true, true)
	isStale: boolean; // heartbeat > 5 min ago
	hasHeartbeat: boolean;
	heartbeatUnavailable: boolean; // true when heartbeat endpoint returned a non-404 error
	jobAgentVersion: string | null;
	databaseVersion: number | null;
	ipAddress: string | null;
	hostName: string | null;
	dbServerName: string | null;
	dbName: string | null; // connected core-one database name (shown without expanding)
	oneConfiguration: string | null; // serialized core-one configuration (raw JSON string)
	filters: { name: string | null; value: string | null }[]; // worker scope filters (name + value)
}

export interface ITableErrorDisplay {
	tableSelf: string; // table self-link for navigation to the table row
	tableName: string;
	failure: string;
	failureScope: string | null;
	failureKind: string;
	isRetriable: boolean;
	at: number | null; // epoch-ms the error was last updated (or recorded); null when the API sends no time
	atFormatted: string;
}

export interface ITransitionDisplay {
	id: string;
	source: string | null;
	event: string | null;
	action: string | null;
	outcome: string | null;
	previousState: string | null;
	nextState: string | null;
	context: string | null;
	count: number;
	lastSeenFormatted: string; // formatted via aciesDateFormat(date, true, true)
	lastSeen: Date; // raw date for sorting
}
